import type { PreviewVoxel } from '@/components/StartVolumePreview'
import { rasterizeCube, rasterizeCylinder, rasterizeSphere } from '@/shapes'
import { normalizeHexNumber } from '@/utils/color'
import { parseKey, type Position } from '@/voxelMath'
import type { VoxelData } from '@/voxelTypes'

/**
 * Start-card sample at true scale: the same few things (a ball, a block, a little block, a post) at
 * fixed sizes in voxels, clustered near the middle of the floor. On a big document they are small;
 * on a small one they nearly fill it, which is the honest picture of what a start size means.
 */
export function createTrueScaleSampleVoxels(
  bounds: Position,
  colors: readonly number[],
  sample: readonly TrueScaleSampleItem[] = TRUE_SCALE_SAMPLE,
): PreviewVoxel[] {
  const palette = normalizeColors(colors)
  const voxels = new Map<number, VoxelData>()
  const cx = bounds.x / 2
  const cz = bounds.z / 2
  const maxRadius = Math.max(1, (bounds.y - 1) / 2)
  sample.forEach((item, index) => {
    const color = colorAt(palette, index)
    const x = Math.floor(Math.max(0, Math.min(bounds.x - 1, cx + item.dx)))
    const z = Math.floor(Math.max(0, Math.min(bounds.z - 1, cz + item.dz)))
    switch (item.kind) {
      case 'sphere': {
        const radius = Math.min(item.size, maxRadius)
        stampSphere(voxels, bounds, { x, y: radius, z }, radius, color)
        return
      }
      case 'cube': {
        const half = Math.min(item.size, Math.floor(bounds.y / 2))
        stampCube(voxels, bounds, { x, y: half, z }, half, color)
        return
      }
      case 'post': {
        const height = Math.min(item.height ?? item.size * 4, bounds.y)
        stampCylinder(voxels, bounds, { x, y: height / 2, z }, item.size, height, color, 'y')
        return
      }
    }
  })
  return Array.from(voxels.entries())
    .sort(([a], [b]) => a - b)
    .map(([key, voxel]) => ({
      key: String(key),
      position: parseKey(key),
      color: typeof voxel.color === 'number' ? normalizeHexNumber(voxel.color) : colorAt(palette, 0),
    }))
}

/** Sizes in voxels (sphere radius, cube half-side, post radius), offsets from the floor's centre. */
export type TrueScaleSampleItem = { kind: 'sphere' | 'cube' | 'post'; size: number; height?: number; dx: number; dz: number }

/** Just a voxel ball and a voxel block (the home lab's new-world tile). */
export const BALL_AND_BLOCK_SAMPLE: readonly TrueScaleSampleItem[] = [
  { kind: 'sphere', size: 5, dx: 6, dz: 0 },
  { kind: 'cube', size: 3, dx: -6, dz: 0 },
]

const TRUE_SCALE_SAMPLE: readonly TrueScaleSampleItem[] = [
  { kind: 'sphere', size: 5, dx: 7, dz: -1 },
  { kind: 'cube', size: 3, dx: -5, dz: 1 },
  { kind: 'cube', size: 2, dx: -12, dz: 6 },
  { kind: 'post', size: 1.5, height: 9, dx: 0, dz: 8 },
  { kind: 'cube', size: 1, dx: 6, dz: 9 },
  { kind: 'sphere', size: 2, dx: -10, dz: -6 },
]

function stampSphere(
  voxels: Map<number, VoxelData>,
  bounds: Position,
  center: Position,
  radius: number,
  color: number
): void {
  const voxel: VoxelData = { type: 'solid', color: normalizeHexNumber(color) }
  rasterizeSphere(center, radius, voxel, bounds, (key, value) => {
    voxels.set(key, value)
  })
}

function stampCylinder(
  voxels: Map<number, VoxelData>,
  bounds: Position,
  center: Position,
  radius: number,
  height: number,
  color: number,
  axis: 'x' | 'y' | 'z'
): void {
  const voxel: VoxelData = { type: 'solid', color: normalizeHexNumber(color) }
  rasterizeCylinder(center, radius, height, voxel, (key, value) => {
    voxels.set(key, value)
  }, bounds, axis)
}

function stampCube(
  voxels: Map<number, VoxelData>,
  bounds: Position,
  center: Position,
  radius: number,
  color: number
): void {
  const voxel: VoxelData = { type: 'solid', color: normalizeHexNumber(color) }
  rasterizeCube(center, radius, voxel, bounds, (key, value) => {
    voxels.set(key, value)
  })
}

function colorAt(palette: readonly number[], index: number): number {
  return palette[index % palette.length] ?? 0xffffff
}

function normalizeColors(colors: readonly number[]): number[] {
  const normalized = colors
    .filter((color) => typeof color === 'number' && Number.isFinite(color))
    .map(normalizeHexNumber)
  return normalized.length > 0 ? normalized : [0xffffff]
}
